import moment from "moment";
import "moment/locale/es";
import { colorDeSegmento } from "../Components/rutaPickingColores";
import { construirSvgMiniRack, posicionRackTexto, puedeDibujarMiniRack } from "../Components/miniMapaRack";

moment.locale("es");

const COLOR_ENCIMADO_PDF = "#ef4444";
const COLOR_ACTUAL_PDF = "#f97316";
const COLOR_RACK_DESTINO_PDF = "#f97316";

//Diagrama del almacén con la ruta resaltada, como SVG embebido (no una imagen) - mismos colores por tramo
//que el mini-mapa en pantalla (VisorRutaPickingMapa.js), para que lo impreso coincida con lo que se vio en
//el drawer. Se usa SVG (no el tipo "canvas" de pdfmake) porque necesitamos texto (el número de cada parada)
//posicionado con precisión sobre cada nodo, y "canvas" no soporta texto. Si no hay datosMapa (ruta simple,
//sin grafo), no se dibuja nada aquí - el PDF sigue funcionando solo con la tabla de abajo.
const construirDiagramaRuta = ({ datosMapa, segmentosCamino, paradas, indiceParadaActual }) => {
  if (!datosMapa || !datosMapa.nodos || datosMapa.nodos.length === 0) return null;

  const { nodos = [], conexiones = [], racks = [] } = datosMapa;
  const ANCHO = 500;
  const ALTO = 240;
  const MARGEN = 10;

  const todasX = [...nodos.map((n) => n.x), ...racks.map((r) => r.x), ...racks.map((r) => r.x + (r.ancho || 0))];
  const todasY = [...nodos.map((n) => n.y), ...racks.map((r) => r.y), ...racks.map((r) => r.y + (r.alto || 0))];
  const minX = Math.min(...todasX);
  const maxX = Math.max(...todasX);
  const minY = Math.min(...todasY);
  const maxY = Math.max(...todasY);
  const anchoReal = Math.max(1, maxX - minX);
  const altoReal = Math.max(1, maxY - minY);
  const escala = Math.min((ANCHO - MARGEN * 2) / anchoReal, (ALTO - MARGEN * 2) / altoReal);

  const escalarX = (x) => MARGEN + (x - minX) * escala;
  const escalarY = (y) => MARGEN + (y - minY) * escala;

  const segmentosPorConexion = new Map();
  (segmentosCamino || []).forEach((valorSegmento) => {
    (valorSegmento.conexionIds || []).forEach((conexionId) => {
      const llave = String(conexionId);
      if (!segmentosPorConexion.has(llave)) segmentosPorConexion.set(llave, []);
      segmentosPorConexion.get(llave).push(valorSegmento.numeroSegmento);
    });
  });

  const nodosPorId = new Map(nodos.map((valorNodo) => [String(valorNodo._id), valorNodo]));
  const numeroParadaPorNodo = new Map();
  (paradas || []).forEach((valorParada) => {
    if (valorParada.nodoAccesoId) numeroParadaPorNodo.set(String(valorParada.nodoAccesoId), valorParada.orden);
  });

  // Rack(s) a los que hay que ir para la parada actual (si se imprime a media ruta) - se resaltan igual
  // que en pantalla (ver VisorRutaPickingMapa.js/RackCanvas.js).
  const rackIdsDestacados = new Set(
    (indiceParadaActual != null ? paradas?.[indiceParadaActual]?.rackIds : []) || []
  );

  // Número de picking (orden de la parada) y tramo que le corresponde a cada rack visitado - un rack puede
  // estar ligado al mismo nodo de acceso que otro (racks a ambos lados de un pasillo), así que solo se toma
  // el primer match. `indice` (0-based, alineado 1:1 con paradas/segmentosCamino) se usa para el color del
  // tramo, igual que en las conexiones de arriba.
  const numeroParadaPorRack = new Map();
  const numeroSegmentoPorRack = new Map();
  (paradas || []).forEach((valorParada, indice) => {
    (valorParada.rackIds || []).forEach((rackId) => {
      const llave = String(rackId);
      if (numeroParadaPorRack.has(llave)) return;
      numeroParadaPorRack.set(llave, valorParada.orden);
      numeroSegmentoPorRack.set(llave, indice + 1);
    });
  });

  const partes = [];

  racks.forEach((valorRack) => {
    const rackIdStr = String(valorRack._id);
    const destacado = rackIdsDestacados.has(rackIdStr);
    const numeroParadaRack = numeroParadaPorRack.get(rackIdStr);
    const numeroSegmentoRack = numeroSegmentoPorRack.get(rackIdStr);
    const enRuta = numeroParadaRack != null;
    const colorRack = destacado ? COLOR_RACK_DESTINO_PDF : enRuta ? colorDeSegmento(numeroSegmentoRack) : "#cccccc";
    const x = escalarX(valorRack.x);
    const y = escalarY(valorRack.y);
    const w = (valorRack.ancho || 0) * escala;
    const h = (valorRack.alto || 0) * escala;
    partes.push(
      `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${enRuta || destacado ? colorRack + "33" : "#f0f0f0"}" stroke="${colorRack}" stroke-width="${destacado ? 2 : enRuta ? 1.5 : 0.5}" />`
    );
    if (numeroParadaRack != null) {
      partes.push(
        `<text x="${x + w / 2}" y="${y + h / 2}" text-anchor="middle" dominant-baseline="middle" font-size="8" font-weight="bold" fill="${colorRack}">${numeroParadaRack}</text>`
      );
    }
  });

  conexiones.forEach((valorConexion) => {
    const origen = nodosPorId.get(String(valorConexion.nodoOrigenId));
    const destino = nodosPorId.get(String(valorConexion.nodoDestinoId));
    if (!origen || !destino) return;
    const segmentosQueLaUsan = segmentosPorConexion.get(String(valorConexion._id)) || [];
    const encimada = segmentosQueLaUsan.length > 1;
    const enRuta = segmentosQueLaUsan.length > 0;
    const numeroSegmentoActual = indiceParadaActual != null ? indiceParadaActual + 1 : null;
    const esActual = numeroSegmentoActual != null && segmentosQueLaUsan.includes(numeroSegmentoActual);
    const color = esActual
      ? COLOR_ACTUAL_PDF
      : encimada
      ? COLOR_ENCIMADO_PDF
      : enRuta
      ? colorDeSegmento(segmentosQueLaUsan[0])
      : "#dddddd";
    partes.push(
      `<line x1="${escalarX(origen.x)}" y1="${escalarY(origen.y)}" x2="${escalarX(destino.x)}" y2="${escalarY(destino.y)}" stroke="${color}" stroke-width="${esActual ? 3 : enRuta ? 2 : 0.5}" />`
    );
  });

  nodos.forEach((valorNodo) => {
    const numeroParada = numeroParadaPorNodo.get(String(valorNodo._id));
    const cx = escalarX(valorNodo.x);
    const cy = escalarY(valorNodo.y);
    const radio = numeroParada ? 6 : 1.5;
    partes.push(`<circle cx="${cx}" cy="${cy}" r="${radio}" fill="${numeroParada ? "#00BFD6" : "#999999"}" />`);
    if (numeroParada) {
      partes.push(
        `<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="middle" font-size="6.5" font-weight="bold" fill="#ffffff">${numeroParada}</text>`
      );
    }
  });

  const svg = `<svg width="${ANCHO}" height="${ALTO}" xmlns="http://www.w3.org/2000/svg">${partes.join("")}</svg>`;
  return { svg, width: ANCHO, height: ALTO };
};

//Construye el contenido pdfMake de una ruta de picking: paradas ordenadas por el recorrido físico del
//almacén, cada una con los insumos/cantidades a recolectar ahí. faltantes/sinUbicacionAsignada se listan
//aparte porque no son paradas reales (no tienen una ubicación física asignable en la ruta).
export const construirContenidoRutaPicking = ({
  titulo,
  proyectoNombre,
  paradas = [],
  faltantes = [],
  sinUbicacionAsignada = [],
  datosMapa,
  segmentosCamino = [],
  indiceParadaActual,
  etiquetaAccion = "Recolectado",
}) => {
  const fecha = moment().format("DD/MM/YYYY HH:mm");

  const headerTabla = [
    { text: "#", style: "thHeader", alignment: "center" },
    { text: "Ubicación", style: "thHeader" },
    { text: "Distancia", style: "thHeader", alignment: "right" },
    { text: "Insumo", style: "thHeader" },
    { text: "Cantidad", style: "thHeader", alignment: "right" },
    { text: "Ubicación en rack", style: "thHeader", alignment: "center" },
    { text: etiquetaAccion, style: "thHeader", alignment: "center" },
  ];

  const filas = [];
  paradas.forEach((parada, indexParada) => {
    const etiquetaUbicacion = `${parada.clave || ""}${parada.nombre ? " - " + parada.nombre : ""}`;
    const ubicacionesDeParada = Array.from(new Set((parada.insumos || []).map((i) => i.claveUbicacion).filter(Boolean)));
    const celdaUbicacionParada = ubicacionesDeParada.length
      ? { stack: [{ text: etiquetaUbicacion, bold: true }, { text: `Ubicaciones: ${ubicacionesDeParada.join(", ")}`, fontSize: 7, color: "#888888", margin: [0, 1, 0, 0] }] }
      : { text: etiquetaUbicacion, bold: true };
    const etiquetaDistancia = parada.distanciaDesdeAnteriorMetros != null ? `${parada.distanciaDesdeAnteriorMetros.toFixed(1)} m` : "";
    (parada.insumos || []).forEach((insumo, indexInsumo) => {
      const etiquetaInsumo = `${insumo.clave || ""}${insumo.descripcion ? " - " + insumo.descripcion : ""}`;
      const posicion = posicionRackTexto(insumo);
      let celdaRack = { text: "", style: "tdCell" };
      if (posicion) {
        const stackRack = [];
        if (puedeDibujarMiniRack(insumo)) {
          const svg = construirSvgMiniRack({
            numeroColumnas: insumo.rackNumeroColumnas,
            numeroNiveles: insumo.rackNumeroNiveles,
            columna: insumo.columna,
            nivelRack: insumo.nivelRack,
            ancho: 80,
            alto: 50,
          });
          stackRack.push({ svg, width: 80, height: 50, alignment: "center" });
        }
        stackRack.push({ text: posicion, fontSize: 7, color: "#888888", alignment: "center", margin: [0, stackRack.length ? 1 : 0, 0, 0] });
        celdaRack = { stack: stackRack, style: "tdCell", alignment: "center" };
      }
      filas.push([
        { text: indexInsumo === 0 ? String(indexParada + 1) : "", style: "tdCell", alignment: "center" },
        indexInsumo === 0 ? { ...celdaUbicacionParada, style: "tdCell" } : { text: "", style: "tdCell" },
        { text: indexInsumo === 0 ? etiquetaDistancia : "", style: "tdCell", alignment: "right" },
        { text: etiquetaInsumo, style: "tdCell" },
        { text: String(insumo.cantidad), style: "tdCell", alignment: "right" },
        celdaRack,
        { text: "[  ]", style: "tdCell", alignment: "center" },
      ]);
    });
  });

  if (filas.length === 0) {
    filas.push([
      { text: "No hay paradas para esta ruta", colSpan: 7, alignment: "center", italics: true, style: "tdCell" },
      {}, {}, {}, {}, {}, {},
    ]);
  }

  const contenido = [
    { text: titulo || "Ruta de picking", style: "tableHeaderIzquierda", margin: [0, 0, 0, 4] },
    { text: `${proyectoNombre ? "Almacén: " + proyectoNombre + "  ·  " : ""}Fecha: ${fecha}`, style: "textoChico", margin: [0, 0, 0, 10] },
  ];

  const diagrama = construirDiagramaRuta({ datosMapa, segmentosCamino, paradas, indiceParadaActual });
  if (diagrama) {
    contenido.push(
      { svg: diagrama.svg, width: diagrama.width, margin: [0, 0, 0, 4] },
      {
        text: "Círculos turquesa numerados = paradas en orden. Racks de colores y numerados = orden de picking (mismo color que su tramo). Rojo = tramo recorrido más de una vez. Naranja = siguiente tramo/rack a donde ir.",
        style: "textoChico",
        margin: [0, 0, 0, 12],
      }
    );
  }

  contenido.push({
    style: "tableExample",
    table: {
      headerRows: 1,
      widths: [16, 85, 38, "*", 40, 95, 32],
      body: [headerTabla, ...filas],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#999999",
      vLineColor: () => "#999999",
      fillColor: (rowIndex) => (rowIndex === 0 ? "#e8e8e8" : null),
      paddingTop: () => 4,
      paddingBottom: () => 4,
      paddingLeft: () => 5,
      paddingRight: () => 5,
    },
  });

  if (sinUbicacionAsignada.length > 0) {
    contenido.push(
      { text: "Sin ubicación asignada (verificar físicamente)", style: "subheader", margin: [0, 14, 0, 4] },
      {
        style: "tableExample",
        table: {
          widths: [60, "*", 55],
          body: [
            [{ text: "Clave", style: "thHeader" }, { text: "Insumo", style: "thHeader" }, { text: "Cantidad", style: "thHeader", alignment: "right" }],
            ...sinUbicacionAsignada.map((insumo) => [
              { text: insumo.clave || "", style: "tdCell" },
              { text: insumo.descripcion || "", style: "tdCell" },
              { text: String(insumo.cantidad), style: "tdCell", alignment: "right" },
            ]),
          ],
        },
        layout: "lightHorizontalLines",
      },
    );
  }

  if (faltantes.length > 0) {
    contenido.push(
      { text: "Faltantes (no hay existencia suficiente en el almacén)", style: "subheader", margin: [0, 14, 0, 4] },
      {
        style: "tableExample",
        table: {
          widths: [60, "*", 55],
          body: [
            [{ text: "Clave", style: "thHeader" }, { text: "Insumo", style: "thHeader" }, { text: "Cantidad", style: "thHeader", alignment: "right" }],
            ...faltantes.map((insumo) => [
              { text: insumo.clave || "", style: "tdCell" },
              { text: insumo.descripcion || "", style: "tdCell" },
              { text: String(insumo.cantidad), style: "tdCell", alignment: "right" },
            ]),
          ],
        },
        layout: "lightHorizontalLines",
      },
    );
  }

  return contenido;
};

export const stylesRutaPicking = {
  subheader: { fontSize: 10, bold: true },
  textoChico: { fontSize: 8, color: "#666" },
  tableHeaderIzquierda: { bold: true, fontSize: 16, color: "black" },
  tableExample: { margin: [0, 5, 0, 5] },
  thHeader: { bold: true, fontSize: 9 },
  tdCell: { fontSize: 9 },
};

const rutaPickingPdf = ({ titulo, proyectoNombre, paradas, faltantes, sinUbicacionAsignada, datosMapa, segmentosCamino, indiceParadaActual, etiquetaAccion }) => ({
  pageSize: "LETTER",
  pageMargins: [30, 35, 30, 40],
  info: {
    title: titulo || "Ruta de picking",
  },
  content: construirContenidoRutaPicking({
    titulo,
    proyectoNombre,
    paradas,
    faltantes,
    sinUbicacionAsignada,
    datosMapa,
    segmentosCamino,
    indiceParadaActual,
    etiquetaAccion,
  }),
  footer: (currentPage, pageCount) => ({
    columns: [
      { text: titulo || "Ruta de picking", alignment: "left", fontSize: 8, color: "#888", margin: [30, 0, 0, 0] },
      { text: `Página ${currentPage} de ${pageCount}`, alignment: "right", fontSize: 8, color: "#888", margin: [0, 0, 30, 0] },
    ],
  }),
  defaultStyle: { fontSize: 9 },
  styles: stylesRutaPicking,
});

export default rutaPickingPdf;
